import { Injectable } from '@angular/core';
import {
  ParkingClient,
  ParkingClientOption,
  ParkingClientPayload,
  ParkingClientRole,
  ParkingClientTableDto,
  ParkingClientTypeOption,
} from '../../models/parking/parking-client.model';

@Injectable({ providedIn: 'root' })
export class MapperParkingClientService {
  createEmpty(siteId: number): ParkingClient {
    return {
      id: 0,
      name: '',
      address: '',
      telephone: '',
      taxId: '',
      contact: '',
      branch: '',
      remark: '',
      clientTypeId: null,
      clientTypeName: '',
      siteId,
      siteName: '',
      parentClientId: null,
      parentClientName: '',
      clientRole: '',
      canSelfManage: false,
      limitTransaction: null,
      limitCard: null,
      stamp: '',
      createUid: null,
      writeUid: null,
      createDate: null,
      writeDate: null,
    };
  }

  mapFromDto(dto: ParkingClientTableDto): ParkingClient {
    const r = dto as unknown as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      name: String(r['name'] ?? r['Name'] ?? ''),
      address: String(r['address'] ?? r['Address'] ?? ''),
      telephone: String(r['telephone'] ?? r['Telephone'] ?? ''),
      taxId: String(r['taxId'] ?? r['TaxId'] ?? ''),
      contact: String(r['contact'] ?? r['Contact'] ?? ''),
      branch: String(r['branch'] ?? r['Branch'] ?? ''),
      remark: String(r['remark'] ?? r['Remark'] ?? ''),
      clientTypeId: (r['clientTypeId'] ?? r['ClientTypeId'] ?? null) as number | null,
      clientTypeName: String(r['clientTypeName'] ?? r['ClientTypeName'] ?? ''),
      siteId: (r['siteId'] ?? r['SiteId'] ?? null) as number | null,
      siteName: String(r['siteName'] ?? r['SiteName'] ?? ''),
      parentClientId: (r['parentClientId'] ?? r['ParentClientId'] ?? null) as number | null,
      parentClientName: String(r['parentClientName'] ?? r['ParentClientName'] ?? ''),
      clientRole: this.normalizeRole(String(r['clientRole'] ?? r['ClientRole'] ?? '')),
      canSelfManage: !!(r['canSelfManage'] ?? r['CanSelfManage']),
      limitTransaction: (r['limitTransaction'] ?? r['LimitTransaction'] ?? null) as number | null,
      limitCard: (r['limitCard'] ?? r['LimitCard'] ?? null) as number | null,
      stamp: String(r['stamp'] ?? r['Stamp'] ?? ''),
      createUid: (r['createUid'] ?? r['CreateUid'] ?? null) as number | null,
      writeUid: (r['writeUid'] ?? r['WriteUid'] ?? null) as number | null,
      createDate: this.toDate(r['createDate'] ?? r['CreateDate']),
      writeDate: this.toDate(r['writeDate'] ?? r['WriteDate']),
    };
  }

  mapToPayload(client: ParkingClient, userId: number, menuId: number): ParkingClientPayload {
    return {
      userId,
      menuId,
      siteId: client.siteId ?? 0,
      name: client.name.trim(),
      address: client.address?.trim() || undefined,
      telephone: client.telephone?.trim() || undefined,
      taxId: client.taxId?.trim() || undefined,
      contact: client.contact?.trim() || undefined,
      branch: client.branch?.trim() || undefined,
      remark: client.remark?.trim() || undefined,
      clientTypeId: client.clientTypeId,
      parentClientId: client.parentClientId,
      clientRole: client.clientRole || undefined,
      canSelfManage: client.canSelfManage,
      limitTransaction: client.limitTransaction,
      limitCard: client.limitCard,
      stamp: client.stamp?.trim() || undefined,
    };
  }

  mapClientTypeOptions(res: unknown): ParkingClientTypeOption[] {
    const list = Array.isArray(res) ? res : [];
    return (list as Record<string, unknown>[]).map((r) => ({
      id: Number(r['id'] ?? r['Id'] ?? 0),
      name: String(r['name'] ?? r['Name'] ?? ''),
    }));
  }

  mapClientOptions(res: unknown): ParkingClientOption[] {
    const list = Array.isArray(res) ? res : [];
    return (list as Record<string, unknown>[]).map((r) => ({
      id: Number(r['id'] ?? r['Id'] ?? 0),
      label: String(r['label'] ?? r['Label'] ?? r['name'] ?? r['Name'] ?? ''),
    }));
  }

  roleLabel(role: string | null | undefined): string {
    switch ((role || '').toLowerCase()) {
      case 'distributor': return 'ฝ่ายอาคาร';
      case 'tenant': return 'ผู้เช่า';
      case 'standalone': return 'อิสระ';
      default: return role || '—';
    }
  }

  private normalizeRole(value: string): ParkingClientRole {
    const v = value.toLowerCase();
    if (v === 'distributor' || v === 'tenant' || v === 'standalone') return v;
    return '';
  }

  private toDate(value: unknown): Date | null {
    if (!value) return null;
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? null : d;
  }
}
